'use client'

import {FC, useEffect} from 'react'
import s from './style.module.css'
import {GameDetailsResponse} from '@app/components/game-player/types/game-player'
import {constructName, getAllSearchParams} from './helper'
import analytics, {CommonGameAttributes, GameLoadCompleteAttributes} from '@app/components/game-player/telemetry/events'
import {useDomino} from './domino/domino-visiblity-context/domino-visiblity-context.client'
import {LanguageCodes} from '@app/i18n/settings'
import {getGameWindow} from '@app/components/game-player/helper'
import {BasicPlayer} from '@gamezop/game-player'
import {client} from '@app/api/auth'
import {GameProps} from '@gamezop/game-player'
import {DEFAULT_PROPERTY_CODE, USER_JOURNEY_GROUP} from '@app/constants/global'
import {usePathname, useRouter, useSearchParams} from 'next/navigation'
import useSendAdDataToGameSDK from './hooks/use-send-ad-data'
import {updateLastPlayedAction} from './last-played-action'
import {muteGame} from './game-functions'
import useAdManager from './hooks/use-ad-manager'
import useGameState from './hooks/use-game-state'
import {useGamePlayerAds} from './context/gameplayer-ads-context/gameplayer-ads-context'
import {useTabSwitchHandler} from './hooks/use-tab-switch-handler'
import {usePlayButton} from '../gameplayer-page/game-image-and-play-btn/play-btn/play-button-context'

interface Props {
  game: GameDetailsResponse
  isMobile: boolean
  propertyCode: string
  lang: LanguageCodes
  isAdEnabled: boolean
  accountData: Record<string, any>
}

export enum GamePlayerTheme {
  Dark = 'dark',
  Light = 'light',
}

const GamePlayerCometClient: FC<Props> = ({game, isMobile, propertyCode, lang, isAdEnabled, accountData}) => {
  const {setDominoVisibility, setRetryGame} = useDomino()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const {showMidrollAd, showRewardedAd} = useGamePlayerAds()
  const gamePlayerLogo = propertyCode === 'demoapp22' ? 'https://static.gamezop.com/demoapp22/logo-light.png' : ''
  const {gameScreenShown} = usePlayButton()

  useSendAdDataToGameSDK({isAdEnabled})
  useTabSwitchHandler()

  useEffect(() => {
    client.setClientOptions({
      platform: 'gamezop',
      pubId: propertyCode || DEFAULT_PROPERTY_CODE,
      baseURL: process.env.NEXT_PUBLIC_ACCOUNT_SERVICE_URL || '',
    })
  }, [propertyCode])

  const sendLoginInfo = (user: Record<string, any>) => {
    if (user) {
      try {
        const gameWind = getGameWindow()
        gameWind.postMessage({event: 'user-login-info', data: user})
      } catch (error) {
        console.log('Error when sending login data: ', error)
      }
    }
  }

  const isLoggedIn = async () => {
    const isLoggedIn = !!accountData
    if (isLoggedIn) {
      const {accCode: userId, firstName, lastName, picture} = accountData
      const name = constructName(firstName, lastName)
      const user = {
        userId,
        name: name || 'Player',
        photo: picture || 'https://static.gamezop.com/comet/assets/img/default_avatar.png',
      }
      sendLoginInfo(user)
    }
  }

  const login = () => {
    const currentParams = getAllSearchParams(searchParams)
    let uri = `/login?redirect=${encodeURIComponent(pathname || '/')}`
    const queryString = Object.entries(currentParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')

    if (queryString) {
      uri = `${uri}&${queryString}`
    }

    try {
      router.push(uri)
    } catch (error) {
      console.log('Error when redirecting', error)
    }
  }

  // currently not needed
  const logout = () => {
    console.log('TBA')
  }

  function handleDominoCb() {
    setDominoVisibility(true)
  }
  const updateGameDataWithEnglishName = (gameData: CommonGameAttributes) => {
    return {
      ...gameData,
      gameName: game.nameEn,
    }
  }

  const updateGameLoadCompleteData = (gameData: GameLoadCompleteAttributes) => {
    return {
      ...gameData,
      gameName: game.nameEn,
    }
  }

  const sendOnGameOver = (gameData: CommonGameAttributes) => {
    setDominoVisibility(true)
    analytics.sendOnGamePlayOver(updateGameDataWithEnglishName(gameData))
  }
  const sendOnGameStart = (gameData: CommonGameAttributes) => {
    setDominoVisibility(false)
    updateLastPlayedAction({propertyCode, gameCode: game.code})
    analytics.sendOnGamePlayStart(updateGameDataWithEnglishName(gameData))
  }

  const gameEvent = {
    over: (gameData: CommonGameAttributes) => sendOnGameOver(updateGameDataWithEnglishName(gameData)),
    loaded: (gameData: GameLoadCompleteAttributes) => analytics.sendOnGameLoaded(updateGameLoadCompleteData(gameData)),
    levelUp: (gameData: CommonGameAttributes) =>
      analytics.sendOnGamePlayLevelUp(updateGameDataWithEnglishName(gameData)),
    start: (gameData: CommonGameAttributes) => sendOnGameStart(gameData),
    retry: (gameData: CommonGameAttributes) => analytics.sendOnGamePlayRetry(updateGameDataWithEnglishName(gameData)),
    loading: (gameData: CommonGameAttributes) => analytics.sendOnGameLoading(updateGameDataWithEnglishName(gameData)),
    delayed: (gameData: CommonGameAttributes) => analytics.sendOnGameDelayed(updateGameDataWithEnglishName(gameData)),
  }

  const gameDetails: GameProps['gameDetails'] = {
    code: game.code,
    name: game.name,
    nameEn: game.nameEn,
    url: game.url,
    lang: game.lang,
    isPortrait: game.isPortrait,
    description: game.description,
    category: '',
    height: game.height,
    width: game.width,
  }

  const muteGameOnLoad = () => {
    if (!gameScreenShown) {
      muteGame()
    }
  }

  useAdManager({onShowMidroll: showMidrollAd, onShowRewarded: showRewardedAd, propertyCode})
  useGameState({
    onGameOver: [showMidrollAd],
    onGameLoaded: [muteGameOnLoad],
    onLevelUp: [showMidrollAd],
  })

  return (
    <div className={s.game_parent}>
      <BasicPlayer
        params={{
          lang,
          userJourneyGroup: USER_JOURNEY_GROUP,
        }}
        pubId={propertyCode}
        gameDetails={gameDetails}
        isMobile={isMobile}
        gameEvent={gameEvent}
        retryGameCallback={setRetryGame}
        handleDominoCb={handleDominoCb}
        gameplayerTheme='dark'
        isAdsEnabled={isAdEnabled}
        logoUrl={gamePlayerLogo}
        auth={{
          login,
          isLoggedIn,
          logout,
        }}
      />
    </div>
  )
}

export default GamePlayerCometClient
